import { contentfulProps } from 'assets/mockData/emptyTypes';

import Container from 'quarks/Container';
import Flex from 'quarks/Flex';
import Grid from 'quarks/Grid';
import Heading from 'quarks/Heading';
import Image from 'quarks/Image';
import Paragraph from 'quarks/Paragraph';
import Text from 'quarks/Text';
import type { GridProps } from 'quarks/interpolations/grid';

import Breadcrumbs from 'molecules/Breadcrumbs';
import Section from 'molecules/Section/Section';

import Form from 'components/Forms/Form';
import List from 'components/List';
import LegalDisclaimer from 'components/ResourceComponents/LegalDisclaimer';

import type { ContentfulTemplateDownload } from 'graphqlTypes';
import type { FC } from 'react';

interface DownloadProps extends GridProps, ContentfulTemplateDownload {}

const DownloadHero: FC<DownloadProps> = ({
  id,
  title,
  featuredImage,
  category,
  excerpt,
  takeawaysList,
  asset,
  ...props
}) => {
  const breadcrumbs = [{ label: 'Downloads', link: '/resources' }, { label: title || '' }];

  return (
    <Section
      id={id}
      paddingTopMobile={32}
      paddingTopTablet={72}
      paddingTopDesktop={96}
      paddingBottomMobile={56}
      paddingBottomDesktop={96}
    >
      <Grid
        {...props}
        paddingBottom={40}
        gridTemplateColumns="1fr"
        width="100%"
        lg={{ gridTemplateColumns: '1fr 1fr', paddingBottom: 96 }}
      >
        <Container position="relative">
          <Container
            position="absolute"
            backgroundColor="primary-900"
            bottom="-350px"
            right="-50px"
            height="2000px"
            width="2000px"
            zIndex={0}
            lg={{
              bottom: 0,
              right: 0,
              height: '2000px',
              width: '50vw',
            }}
          />
          <Flex
            width="100%"
            flexDirection="column"
            flexWrap="wrap"
            paddingBottom={48}
            lg={{ maxWidth: '470px', paddingRight: 24, paddingBottom: 96 }}
            xl={{ paddingRight: 0 }}
            position="relative"
            zIndex={10}
          >
            <Breadcrumbs breadcrumbs={breadcrumbs} backgroundIsDark marginBottom={32} md={{ marginBottom: 48 }} />
            {category && (
              <Text
                id="resource-category"
                textStyle="eyebrowLg"
                fontWeight="bold"
                textColor="primary-200"
                marginBottom={8}
                md={{ marginBottom: 16 }}
              >
                {category.name}
              </Text>
            )}
            {title && (
              <Heading
                id="resource-title"
                textStyle="sm"
                fontWeight="bold"
                textColor="common-white"
                marginBottom={24}
                md={{ textStyle: 'md', fontWeight: 'bold', marginBottom: 32 }}
                lg={{ textStyle: 'lg', fontWeight: 'bold' }}
              >
                {title}
              </Heading>
            )}
            {excerpt && (
              <Paragraph
                id="resource-excerpt"
                fontSize="textLg"
                textColor="gray-200"
                marginBottom={24}
                md={{ marginBottom: 32 }}
              >
                {excerpt.excerpt}
              </Paragraph>
            )}
            {featuredImage && (
              <Image
                image={featuredImage}
                alt={featuredImage.title || 'Download Feature Image'}
                width="100%"
                borderRadius="8px"
                loading="eager"
              />
            )}
          </Flex>
        </Container>
        <Grid
          position="relative"
          lg={{ marginLeft: 'auto', gridColumn: 2, gridRowStart: 1, gridRowEnd: 3, paddingLeft: 24 }}
          xl={{ paddingLeft: 0 }}
        >
          <Container
            lg={{ maxWidth: '470px' }}
            position="sticky"
            top="calc(var(--header-height, 100px) + 16px)"
            width="100%"
            height="fit-content"
            boxShadow="0px 20px 24px -4px rgba(35, 35, 37, 0.08), 0px 8px 8px -4px rgba(35, 35, 37, 0.03)"
            borderRadius="3px"
          >
            {asset && (
              <Form
                {...contentfulProps}
                textAlignment="center"
                variant="Gated Content Download"
                asset={asset}
                heading="Download Your eBook Today"
              />
            )}
          </Container>
        </Grid>
        <Flex
          id="resource-body-content"
          flexDirection="column"
          paddingTop={32}
          lg={{ maxWidth: '470px', paddingTop: 96 }}
        >
          <Heading
            textStyle="xs"
            fontWeight="bold"
            textColor="primary-900"
            md={{ textStyle: 'md', fontWeight: 'bold' }}
          >
            The Takeaways
          </Heading>
          {takeawaysList && <List {...takeawaysList} />}
        </Flex>
      </Grid>
      <LegalDisclaimer />
    </Section>
  );
};

export default DownloadHero;
